import React from 'react';
import styled from 'styled-components';
import { adminTheme } from '../../theme-admin';

const ChartWrapper = styled.div`
  background: ${adminTheme.colors.background.primary};
  border-radius: ${adminTheme.borderRadius.xl};
  padding: ${adminTheme.spacing.xl};
  box-shadow: ${adminTheme.shadows.md};
  border: 1px solid ${adminTheme.colors.gray[200]};
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${adminTheme.spacing.lg};
`;

const ChartTitle = styled.h3`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0;
`;

const ChartSubtitle = styled.p`
  font-size: ${adminTheme.fontSizes.sm};
  color: ${adminTheme.colors.gray[600]};
  margin: 4px 0 0;
`;

const ChartBody = styled.div`
  position: relative;
`;

const SimpleBarChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: 200px;
  padding: ${adminTheme.spacing.md} 0;
`;

const Bar = styled.div`
  flex: 1;
  background: ${props => props.$gradient || adminTheme.gradients.primary};
  border-radius: ${adminTheme.borderRadius.md} ${adminTheme.borderRadius.md} 0 0;
  height: ${props => props.$height || '50%'};
  position: relative;
  transition: ${adminTheme.transitions.normal};
  animation: scaleIn 0.5s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: both;
  transform-origin: bottom;

  &:hover {
    transform: scaleY(1.05);
    filter: brightness(1.1);
  }
`;

const BarLabel = styled.div`
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${adminTheme.fontSizes.xs};
  color: ${adminTheme.colors.gray[600]};
  white-space: nowrap;
`;

const BarValue = styled.div`
  position: absolute;
  top: -24px;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[900]};
  white-space: nowrap;
`;

export function ChartCard({ title, subtitle, children, actions }) {
  return (
    <ChartWrapper>
      <ChartHeader>
        <div>
          <ChartTitle>{title}</ChartTitle>
          {subtitle && <ChartSubtitle>{subtitle}</ChartSubtitle>}
        </div>
        {actions && <div>{actions}</div>}
      </ChartHeader>
      <ChartBody>{children}</ChartBody>
    </ChartWrapper>
  );
}

export function BarChart({ data, gradient }) {
  if (!data || data.length === 0) return null;

  const maxValue = Math.max(...data.map(d => d.value));

  return (
    <SimpleBarChart>
      {data.map((item, index) => (
        <Bar
          key={index}
          $height={`${(item.value / maxValue) * 100}%`}
          $gradient={gradient || adminTheme.gradients.blue}
          $delay={`${index * 0.1}s`}
        >
          <BarValue>{item.value}</BarValue>
          <BarLabel>{item.label}</BarLabel>
        </Bar>
      ))}
    </SimpleBarChart>
  );
}
